
// CourseGenerator.js
import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import axios from "axios";
import {
  Paper,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  CircularProgress,
  Tooltip,
  FormHelperText,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Drawer,
  Toolbar,
  List,
  ListItem,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Alert
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SaveIcon from "@mui/icons-material/Save";
import SaveAsIcon from "@mui/icons-material/SaveAs";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { v4 as uuidv4 } from "uuid";
import { AuthContext } from "../context/AuthContext";
import { baseUrl } from "../api";
import { RadioGroup, FormControlLabel, Radio } from "@mui/material";
import TemplateManager from "../components/TemplateManager";
import { NetworkStatusContext } from "../context/NetworkStatusContext";
import { InstructionBanner } from "../components/InstructionBanner";
import { BUILT_IN_TEMPLATES } from "../components/BuiltinTemplates";
import { useCredits } from "../context/CreditsContext";

const MIN_LESSONS = 1;
const MAX_LESSONS = 30;

export default function CourseGenerator() {
  const { user } = useContext(AuthContext);
  const { refreshCredits } = useCredits();
  const axiosInstance = useMemo(() => {
    const inst = axios.create({ baseURL: baseUrl });
    inst.interceptors.request.use(async (cfg) => {
      if (user) {
        const token = await user.getIdToken();
        cfg.headers.Authorization = `Bearer ${token}`;
      }
      return cfg;
    });
    return inst;
  }, [user]);

  // network status
  const networkStatus = useContext(NetworkStatusContext);
  const isOffline = networkStatus === "offline";

  // form state
  const [subject, setSubject] = useState("");
  const [audience, setAudience] = useState("");
  const [syllabus, setSyllabus] = useState("");
  const [duration, setDuration] = useState("");
  const [accuracyLevel, setAccuracyLevel] = useState("advanced");
  const [contentStyle, setContentStyle] = useState("custom");
  const [tags, setTags] = useState([]);
  const [selectedTag, setSelectedTag] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [chosenLessonIndex, setChosenLessonIndex] = useState(0);
  const [selectedTemplateId, setSelectedTemplateId] = useState("default");
  const [urlDialogOpen, setUrlDialogOpen] = useState(false);
  const [lessonUrl, setLessonUrl] = useState("");

  const [expandedLesson, setExpandedLesson] = useState(0);
  const lessonRefs = useRef([]);

  // custom prompt fragments state: seed from built-in “default” template
  const [fragments, setFragments] = useState(() => {
    const defaultTpl = BUILT_IN_TEMPLATES.find(t => t.id === "default") || {};
    return (defaultTpl.fragments || []).map((f) => ({
      ...f,
      id: uuidv4(),
      label: f.label,
      text: f.text,
      cssClass: f.cssClass,
      scope: f.scope,
      checked: true,
    }));
  });

   // prompt-builder metadata state: also seed from built-in “default”
   const [templateMeta, setTemplateMeta] = useState(() => {
     const {
       domain,
       sectionsPerLesson,
       readingLevel,
       tone,
       mediaDensity,
       codeLanguage,
       language,
       timePerLesson
     } = BUILT_IN_TEMPLATES.find(t => t.id === "default") || {};
  
     return {
       domain,
       sectionsPerLesson,
       readingLevel,
       tone,
       mediaDensity,
       codeLanguage,
       language,
       timePerLesson
     };
   });

  // derive compiled prompt for payload
  const compiledPrompt = useMemo(() => {
    const orderedFrags = fragments
      .filter(f => f.checked)
      .map(f => ({
        cssClass: f.cssClass,
        text: f.text.trim(),
        scope: f.scope
      }));

    return JSON.stringify({
      templateMeta,
      fragments: orderedFrags
    }, null, 2);
  }, [fragments, templateMeta]);

  // outline workflow
  const [step, setStep] = useState(0);
  const [outline, setOutline] = useState([]);
  const [editedOutline, setEditedOutline] = useState([]);

  // loading
  const [loadingOutline, setLoadingOutline] = useState(false);
  const [loadingCourse, setLoadingCourse] = useState(false);

  // polling & results
  const [courseId, setCourseId] = useState(
    localStorage.getItem("course_id") || null
  );
  const [courseStatus, setCourseStatus] = useState("");
  const [courseProgress, setCourseProgress] = useState({
    completed_lessons: 0,
    total_lessons: 0,
  });
  const [generatedCourse, setGeneratedCourse] = useState(null);

  // in CourseGenerator()
  const [jsonMode, setJsonMode] = useState(false)
  const [jsonText, setJsonText] = useState("")
  const [jsonError, setJsonError] = useState("")

  const [loadingLesson, setLoadingLesson] = useState(false);
  const [audienceCollapsed, setAudienceCollapsed] = useState(true);

  const [showTemplateManager, setShowTemplateManager] = useState(false);


  // fetch RAG tags whenever user picks “enhanced”
  useEffect(() => {
    if (accuracyLevel === "enhanced") {
      axiosInstance.get("/rag/metadata-tags").then((r) => setTags(r.data));
    } else {
      setSelectedTag("");
    }
  }, [accuracyLevel, axiosInstance]);

  // poll function — now captures the full payload on COMPLETED
  const checkCourseStatus = async (id) => {
    try {
      const resp = await axiosInstance.get(`/course_generation_status/${id}`);
      const d = resp.data;
      setCourseStatus(d.status);
      setCourseProgress({
        completed_lessons: d.completed_lessons,
        total_lessons: d.total_lessons,
      });

      if (d.status === "COMPLETED") {
        setGeneratedCourse(d);
        localStorage.removeItem("course_id");
        refreshCredits();
      } else if (d.status === "FAILED") {
        alert("Course generation failed.");
        localStorage.removeItem("course_id");
      } else {
        setTimeout(() => checkCourseStatus(id), 5000);
      }
    } catch (err) {
      console.error(err);
      alert("Failed to check course status");
    }
  };

  // resume polling if there was an in-flight course
  useEffect(() => {
    if (courseId) {
      setStep(2);
      checkCourseStatus(courseId);
    }
  }, [courseId]);

  useEffect(() => {
    function onKeyDown(e) {
      // Ctrl+J or ⌘+J
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "j") {
        e.preventDefault()
        // entering JSON‐edit mode: prefill JSON
        setJsonText(JSON.stringify(editedOutline, null, 2))
        setJsonError("")
        setJsonMode(mode => !mode)
      }
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [editedOutline])

  // STEP 0: generate outline
  const generateOutline = async () => {
    setLoadingOutline(true);
    try {
      const customPrompt = compiledPrompt;
      const payload = {
        subject,
        audience,
        syllabus,
        duration: `${duration} lessons`,
        accuracy_level: accuracyLevel,
        rag_id: accuracyLevel === "enhanced" ? selectedTag : undefined,
        custom_lesson_format: customPrompt,
      };
      const resp = await axiosInstance.post("/generate_outline/", payload);
      refreshCredits();
      setOutline(resp.data);
      setEditedOutline(resp.data);
      setStep(1);
    } catch {
      alert("Failed to generate outline");
    } finally {
      setLoadingOutline(false);
    }
  };

  // listen for Ctrl+P / ⌘+P to toggle TemplateManager
  useEffect(() => {
    function onKeyDown(e) {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "p") {
        e.preventDefault();               // stop browser print
        setShowTemplateManager(v => !v);  // toggle visibility
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  // STEP 1: generate full course
  const generateFullCourse = async () => {
    setLoadingCourse(true);
    try {
      // if custom, use compiledPrompt
      const customPrompt = compiledPrompt;
      const payload = {
        subject,
        audience,
        duration: `${duration} lessons`,
        accuracy_level: accuracyLevel,
        content_style: contentStyle,
        rag_id: accuracyLevel === "enhanced" ? selectedTag : undefined,
        custom_lesson_format: customPrompt,
        outline: editedOutline,
      };
      const resp = await axiosInstance.post("/generate_course/", payload);
      const id = resp.data.course_id;
      setCourseId(id);
      localStorage.setItem("course_id", id);
      setStep(2);
      checkCourseStatus(id);
    } catch {
      alert("Failed to start course generation");
    } finally {
      setLoadingCourse(false);
    }
  };

  const resetGeneration = () => {
    setStep(0);
    setCourseId(null);
    setCourseStatus("");
    setCourseProgress({ completed_lessons: 0, total_lessons: 0 });
    setGeneratedCourse(null);
    localStorage.removeItem("course_id");
  };

  const generateSingleLesson = async (lessonIndex = 0) => {
    if (!editedOutline.length) return;
    setLoadingLesson(true);
    try {
      const lesson = editedOutline[lessonIndex];
      const customPrompt = compiledPrompt;
      const payload = {
        lesson: lesson.lesson,
        subject,
        audience,
        title: lesson.title,
        description: lesson.description,
        duration: lesson.duration,
        topics: lesson.topics,
        content_style: contentStyle,
        accuracy_level: accuracyLevel,
        rag_id: accuracyLevel === "enhanced" ? selectedTag : undefined,
        custom_lesson_format: customPrompt,
        outline: editedOutline,
      };
      const resp = await axiosInstance.post("/generate_lesson", payload);
      setLessonUrl(resp.data.index_url);
      setUrlDialogOpen(true);
      refreshCredits();
    } catch (err) {
      console.error(err);
      alert("Failed to create lesson");
    } finally {
      setLoadingLesson(false);
    }
  };
  

  return (
    <Paper sx={{ p: 3 }}>
      {/* Offline indicator */}
      {isOffline && (
        <InstructionBanner show>
          Network is offline. All inputs are disabled.
        </InstructionBanner>
      )}

      {/* step 0 form */}
      {step === 0 && (
        <>
          <Typography variant="h5" gutterBottom>
            1. Generate & Edit Outline
          </Typography>
          {/* … the existing form for step 0 … */}
          <Box component="form" noValidate autoComplete="off">
            {/* Subject */}
            <FormControl fullWidth margin="normal">
              <TextField
                label="Subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
              <FormHelperText
                component="div"
                sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
              >
                <Tooltip
                  arrow
                  title={
                    <Typography variant="body2">
                      Define the course topic clearly and specifically.<br />
                      <strong>Examples:</strong> <br />
                      • Introductory Python Programming for Data Analysis<br />
                      • Quantum Mechanics I: Wavefunctions &amp; Operators<br />
                      • Advanced Machine Learning with PyTorch<br />
                      • An Illustrated Guide to Generative Pre-Trained Transformers<br />
                    </Typography>
                  }
                  slotProps={{ tooltip: { sx: { maxWidth: 600, whiteSpace: 'normal' } } }}
                >
                  <InfoOutlinedIcon fontSize="small" sx={{ cursor: "pointer" }} />
                </Tooltip>
                <Typography variant="caption" color="text.secondary">
                  Enter the main topic or subject area.
                </Typography>
              </FormHelperText>
            </FormControl>
            {/* Audience */}
            <FormControl fullWidth margin="normal">
              <TextField
                label="Audience"
                multiline
                rows={audienceCollapsed ? 1 : 8}
                value={audience}
                onChange={(e) => setAudience(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      size="small"
                      onClick={() => setAudienceCollapsed((c) => !c)}
                    >
                      {audienceCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                    </IconButton>
                  )
                }}
                sx={{
                  // optional: smoothly animate height
                  transition: "all 150ms ease"
                }}
              />
              <FormHelperText
                component="div"
                sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
              >
                <Tooltip
                  arrow
                  title={
                    <Typography variant="body2">
                      Describe your target learners:<br />
                      <strong>Examples:</strong> <br/>
                      • First-year STEM undergraduates majoring in Data Science<br/>
                      • High-school seniors preparing for AP Physics exams<br/>
                      • Working professionals transitioning into Data Analytics roles<br/>
                      • This guide is intended for students, developers, and AI enthusiasts who are new to large language models and want a clear, visual introduction to how Generative Pre-Trained Transformers (GPTs) work. No prior experience with deep learning is required, though basic familiarity with programming or machine learning concepts may be helpful<br />
                    </Typography>
                  }
                  slotProps={{ tooltip: { sx: { maxWidth: 600, whiteSpace: 'normal' } } }}
                >
                  <InfoOutlinedIcon fontSize="small" sx={{ cursor: "pointer" }} />
                </Tooltip>
                <Typography variant="caption" color="text.secondary">
                  Provide skill level or prior knowledge.
                </Typography>
              </FormHelperText>
            </FormControl>
            {/* Syllabus (optional) */}
            <FormControl fullWidth margin="normal">
              <TextField
                label="Syllabus (optional)"
                multiline
                minRows={2}
                value={syllabus}
                onChange={e => setSyllabus(e.target.value)}
                disabled={isOffline}
                placeholder="List key topics or module titles, one per line"
              />
              <FormHelperText>
                You can paste your syllabus here to guide the outline generator.
              </FormHelperText>
            </FormControl>
            {/* Duration */}
            <FormControl fullWidth margin="normal">
              <InputLabel id="duration-label">Estimated Course Length</InputLabel>
              <Select
                labelId="duration-label"
                value={duration}
                label="Estimated Course Length"
                onChange={(e) => setDuration(e.target.value)}
              >
                {Array.from({ length: MAX_LESSONS - MIN_LESSONS + 1 }, (_, i) => {
                  const w = i + MIN_LESSONS;
                  return (
                    <MenuItem key={w} value={w}>
                      {w} {w === 1 ? "lesson" : "lessons"}
                    </MenuItem>
                  );
                })}
              </Select>
              <FormHelperText>
                Specify # of lessons; each = one hour.
              </FormHelperText>
            </FormControl>

            {/* Accuracy */}
            <FormControl fullWidth margin="normal">
              <InputLabel id="accuracy-label">Accuracy Level</InputLabel>
              <Select
                labelId="accuracy-label"
                value={accuracyLevel}
                label="Accuracy Level"
                onChange={(e) => setAccuracyLevel(e.target.value)}
                disabled={isOffline}
              >
                <MenuItem value="standard">Standard LLM</MenuItem>
                <MenuItem value="advanced">Advanced LLM</MenuItem>
                <MenuItem value="enhanced">Enhanced (LLM + RAG)</MenuItem>
              </Select>
              <FormHelperText component="div" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Tooltip
                  arrow
                  title={
                    <Typography variant="body2" sx={{ maxWidth: 250 }}>
                      <strong>Standard LLM</strong> – fast, standard-level.<br /><br />
                      <strong>Advanced LLM</strong> – slow, advanced-level.<br /><br />
                      <strong>Enhanced (LLM + RAG)</strong> – uses your reference docs for higher accuracy.
                    </Typography>
                  }
                >
                  <InfoOutlinedIcon fontSize="small" sx={{ cursor: "pointer" }} />
                </Tooltip>
                <Typography variant="caption" color="text.secondary">
                  Higher accuracy may increase time & cost.
                </Typography>
              </FormHelperText>
            </FormControl>

            {/* RAG document picker */}
            {accuracyLevel === "enhanced" && (
              <FormControl fullWidth margin="normal">
                <InputLabel id="rag-label">Reference Document</InputLabel>
                <Select
                  labelId="rag-label"
                  value={selectedTag}
                  label="Reference Document"
                  onChange={(e) => setSelectedTag(e.target.value)}
                  disabled={isOffline}
                >
                  {tags.map((t) => (
                    <MenuItem key={t} value={t}>
                      {t}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  Pick the tag group you uploaded under “Reference Uploads.”
                </FormHelperText>
              </FormControl>
            )}

            <FormControl fullWidth margin="normal">
              <Box sx={{ display: step === 0 ? 'block' : 'none' }}>
                <TemplateManager
                  selectedTemplateId={selectedTemplateId}
                  setSelectedTemplateId={setSelectedTemplateId}
                  fragments={fragments}
                  setFragments={setFragments}
                  compiledPrompt={compiledPrompt}
                  templateMeta={templateMeta}
                  setTemplateMeta={setTemplateMeta}
                />
                
                <FormHelperText>
                  Manage your custom prompt templates.
                </FormHelperText>
              </Box>
            </FormControl>

            <Box mt={2} textAlign="right">
              <Button
                variant="contained"
                onClick={generateOutline}
                disabled={
                  loadingOutline ||
                  !subject ||
                  !audience ||
                  !duration ||
                  (accuracyLevel === "enhanced" && !selectedTag)
                }
                startIcon={loadingOutline ? <CircularProgress size={20} /> : null}
              >
                {loadingOutline ? "Generating Outline…" : "Generate Outline"}
              </Button>
            </Box>
          </Box>
        </>
      )}

      {step === 1 && (
        <>
          <Typography variant="h5" gutterBottom>
            2. Edit Outline & Generate Course
          </Typography>

          {editedOutline.map((lesson, idx) => {
            const copy = [...editedOutline];
            return (
              <Accordion
              key={lesson.lesson}
              expanded={expandedLesson === idx}
              onChange={() =>
                setExpandedLesson(expandedLesson === idx ? -1 : idx)
              }
              // store ref for scrolling
              ref={(el) => (lessonRefs.current[idx] = el)}
              sx={{ mb: 1 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Chip label={`#${lesson.lesson}`} size="small" sx={{ mr: 1 }} />
                <Chip
                  label={lesson.title || "Untitled"}
                  size="small"
                  sx={{ mr: 1 }}
                />
                <Chip label={lesson.duration} size="small" />
                <Typography variant="caption" sx={{ ml: 2 }}>
                  {lesson.topics?.length || 0} topics
                </Typography>
              </AccordionSummary>
  
              <AccordionDetails>
                {/* Title */}
                <TextField
                  fullWidth
                  label="Title"
                  value={lesson.title}
                  onChange={(e) => {
                    copy[idx].title = e.target.value;
                    setEditedOutline(copy);
                  }}
                  margin="normal"
                />

                {/* Description */}
                <TextField
                  fullWidth
                  label="Description (optional)"
                  multiline
                  value={lesson.description || ""}
                  onChange={(e) => {
                    copy[idx].description = e.target.value;
                    setEditedOutline(copy);
                  }}
                  margin="normal"
                />

                {/* Duration */}
                <TextField
                  fullWidth
                  label="Duration"
                  value={lesson.duration}
                  onChange={(e) => {
                    copy[idx].duration = e.target.value;
                    setEditedOutline(copy);
                  }}
                  helperText="E.g. “45 minutes”"
                  margin="normal"
                />

                {/* Topics */}
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}               // you can supply a list of common topics if you have one
                  value={lesson.topics || []}
                  onChange={(_, newTopics) => {
                    copy[idx].topics = newTopics;
                    setEditedOutline(copy);
                  }}
                  renderTags={(value, getTagProps) =>
                    value.map((topic, i) => (
                      <Chip
                        key={i}
                        variant="outlined"
                        size="small"
                        label={topic}
                        {...getTagProps({ index: i })}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Topics (optional)"
                      placeholder="Add a topic and press Enter"
                      margin="normal"
                    />
                  )}
                />
              </AccordionDetails>
              </Accordion>
            );
          })}
          {/* JSON editor dialog, only opened by hotkey */}
          <Dialog open={jsonMode} onClose={() => setJsonMode(false)} maxWidth="md" fullWidth>
            <DialogTitle>Edit Outline as JSON</DialogTitle>
            <DialogContent>
              <TextField
                fullWidth
                multiline
                minRows={12}
                value={jsonText}
                onChange={e => setJsonText(e.target.value)}
                error={!!jsonError}
                helperText={jsonError || "Paste or edit your outline array here ([{ title, description }, …])"}
                sx={{ fontFamily: "monospace" }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setJsonMode(false)}>Cancel</Button>
              <Button
                variant="contained"
                onClick={() => {
                  try {
                    const parsed = JSON.parse(jsonText)
                    // basic shape‐check
                    if (
                      !Array.isArray(parsed) ||
                      parsed.some(item => typeof item.title !== "string")
                    ) {
                      throw new Error("Must be an array of { title: string, description?: string }")
                    }
                    setEditedOutline(parsed)
                    setJsonMode(false)
                  } catch (err) {
                    setJsonError(err.message)
                  }
                }}
              >
                Apply
              </Button>
            </DialogActions>
          </Dialog>

          <Box mt={2} display="flex" justifyContent="space-between" gap={2}>
            <Button onClick={() => setStep(0)}>Back to Form</Button>

  
            {/* ─── Prompt‐Template Editor ─── */}
            <Box mb={2} sx={{ display: step === 1 && showTemplateManager ? 'block' : 'none' }}>
              <TemplateManager
                selectedTemplateId={selectedTemplateId}
                setSelectedTemplateId={setSelectedTemplateId}
                fragments={fragments}
                setFragments={setFragments}
                compiledPrompt={compiledPrompt}
                templateMeta={templateMeta}
                setTemplateMeta={setTemplateMeta}
                editOnly={true}
              />
              <FormHelperText>
                Edit Lesson Template
              </FormHelperText>
            </Box>

            <Button
              variant="outlined"
              onClick={() => setDialogOpen(true)}
              disabled={loadingLesson || !editedOutline.length}
              startIcon={loadingLesson ? <CircularProgress size={20} /> : null}
            >
              {loadingLesson ? "Creating Lesson…" : "Generate Single Lesson"}
            </Button>

            <Button
              variant="contained"
              onClick={generateFullCourse}
              disabled={loadingCourse}
              startIcon={loadingCourse ? <CircularProgress size={20} /> : null}
            >
              {loadingCourse ? "Starting Course…" : "Generate Full Course"}
            </Button>
        </Box>
        </>
      )}

      {step === 2 && (
        <>
          <Typography variant="h5" gutterBottom>
            3. Course Generation In Progress
          </Typography>

          <Box mt={2}>
            <Typography variant="body2">
              Status: {courseStatus}
            </Typography>
            <Typography variant="body2">
              {courseProgress.completed_lessons}/
              {courseProgress.total_lessons} lessons completed.
            </Typography>
            <Box mt={1}>
              <LinearProgress
                variant="determinate"
                value={
                  courseProgress.total_lessons
                    ? (100 * courseProgress.completed_lessons) /
                      courseProgress.total_lessons
                    : 0
                }
              />
            </Box>
          </Box>

          <Box mt={2} display="flex" justifyContent="flex-end">
            <Button onClick={resetGeneration}>Reset</Button>
          </Box>

          {generatedCourse && (
            <Box mt={4}>
              <Typography variant="h6" gutterBottom>
                🎉 Course Ready!
              </Typography>
              <Typography>
                Total Lessons: {generatedCourse.total_lessons}
              </Typography>
              <Typography>
                Access it here:{" "}
                <a
                  href={generatedCourse.course_website_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {generatedCourse.course_website_url}
                </a>
              </Typography>
            </Box>
          )}
        </>
      )}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
      >
        <DialogTitle>Select a lesson to generate</DialogTitle>
        <DialogContent dividers>
          {/* ⚠️ Add your warning here */}
          <Alert severity="warning" sx={{ mb: 2 }}>
            Please use the created lesson only for verifying lesson content style. It will not be part of your course.
          </Alert>

          <RadioGroup
            value={chosenLessonIndex}
            onChange={e => setChosenLessonIndex(Number(e.target.value))}
          >
            {editedOutline.map((lesson, idx) => (
              <FormControlLabel
                key={idx}
                value={idx}
                control={<Radio />}
                label={`Lesson ${idx + 1}: ${lesson.title || "(no title)"}`}
              />
            ))}
          </RadioGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={async () => {
              setDialogOpen(false);
              await generateSingleLesson(chosenLessonIndex);
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={urlDialogOpen}
        onClose={() => setUrlDialogOpen(false)}
      >
        <DialogTitle>Lesson Created</DialogTitle>
        <DialogContent dividers>
          <Typography>Preview the lesson at:</Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
            <a
              href={lessonUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {lessonUrl}
            </a>
            <Button
              size="small"
              onClick={() => navigator.clipboard.writeText(lessonUrl)}
            >
              Copy
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUrlDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

    </Paper>
  );
}
